/* eslint-disable jsx-a11y/anchor-has-content */
import { useState, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation, Trans } from 'react-i18next'
import classNames from 'classnames'

import classes from './PasswordChangeForm.module.scss'
import PasswordInputWithIcon from '../../common/form/PasswordInputWithIcon'
import { changePassword } from '../../../actions/userActions'
import { flatServerFormErrors } from '../../../utility/helperFunctions'
import { AccountContext } from '../AccountContext'

const CHANGE_PASSWORD_URL = `${process.env.REACT_APP_OAUTH_PROVIDER_HOST}/profile`

// eslint-disable-next-line complexity
const PasswordChangeForm = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { user } = useContext(AccountContext)
  const currentUser = useSelector(state => state.user.current_user)

  const [isUpdating, setIsUpdating] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState({})

  const isCurrentUser = user.id === currentUser.id
  const isFamilyAdmin = currentUser.type === 'Student' && currentUser.family_admin
  const familyAdminEditingJunior = isFamilyAdmin && !isCurrentUser && !user.family_admin

  const validateForm = formData => {
    setErrors({})
    const formErrors = {}

    const currentPassword = formData.get('currentPassword')
    const newPassword = formData.get('newPassword')
    const confirmPassword = formData.get('confirmPassword')

    if (!currentPassword) {
      formErrors['current_password'] = t('account.password.fieldRequired')
    }

    if (!newPassword) {
      formErrors['new_password'] = t('account.password.fieldRequired')
    }

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      formErrors['confirm_password'] = t('account.password.confirmMismatch')
    }

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors)
      return false
    }

    return true
  }

  const handleSubmitForm = async e => {
    e.preventDefault()
    const formElement = e.target
    const formData = new FormData(formElement)

    if (validateForm(formData)) {
      const passwordData = {
        current_password: formData.get('currentPassword'),
        password: formData.get('newPassword'),
        password_confirmation: formData.get('confirmPassword'),
      }

      try {
        setIsUpdating(true)
        const result = await dispatch(changePassword(user.id, passwordData))

        if (result.type === 'UI_ERROR') {
          if (result.error?.general) {
            toast.error(result.error.general.join(', '))
          } else {
            setErrors(flatServerFormErrors(result?.error))
          }
        } else {
          toast.success(t('account.password.updateSuccess'))
          formElement.reset()
        }
      } catch {
        toast.error(t('account.password.updateFailed'))
      } finally {
        setIsUpdating(false)
        setHasChanges(false)
      }
    }
  }

  const handleDetectChanges = (e) => {
    const formData = new FormData(e.currentTarget)
    const hasChanges_ = !!(
      formData.get('currentPassword') ||
      formData.get('newPassword') ||
      formData.get('confirmPassword')
    )

    if (hasChanges_ !== hasChanges) {
      setHasChanges(hasChanges_)
    }
  }

  return (
    <div className={classNames(classes.container, 'animated fadeIn')}>
      <div className='row mb-3'>
        <h4 className={classes.title}>{t('account.password.title')}</h4>
      </div>

      {familyAdminEditingJunior ? (
        <form onSubmit={handleSubmitForm} onInput={handleDetectChanges}>
          <div className='row mb-3'>
            <label htmlFor='currentPassword'>{t('account.password.currentPassword')}</label>
            <PasswordInputWithIcon
              id='currentPassword'
              name='currentPassword'
              placeholder={t('account.password.enterCurrentPassword')}
              classNames={{ inputContainer: classes.accountInputContainer }}
              error={errors?.current_password}
            />
          </div>
          <div className='row mb-3'>
            <label htmlFor='newPassword'>{t('account.password.newPassword')}</label>
            <PasswordInputWithIcon
              id='newPassword'
              name='newPassword'
              placeholder={t('account.password.enterNewPassword')}
              classNames={{ inputContainer: classes.accountInputContainer }}
              error={errors?.new_password}
            />
          </div>
          <div className={classNames(classes.extraMargin, 'row')}>
            <label htmlFor='confirmPassword'>{t('common.field.confirmPassword')}</label>
            <PasswordInputWithIcon
              id='confirmPassword'
              name='confirmPassword'
              placeholder={t('account.password.enterConfirmPassword')}
              classNames={{ inputContainer: classes.accountInputContainer }}
              error={errors?.confirm_password}
            />
          </div>
          <div className='row'>
            <button type='submit' className='btn btn-primary' disabled={isUpdating || !hasChanges}>
              {isUpdating ? t('account.common.savingChanges') : t('common.action.saveChanges')}
            </button>
          </div>
        </form>
      ) : (
        <div className='row'>
          <p className={classes.infoText}>
            <Trans
              i18nKey='account.password.changePrompt'
              components={{ a: <a href={CHANGE_PASSWORD_URL} target='_blank' rel='noopener noreferrer' /> }}
            />
          </p>
        </div>
      )}
    </div>
  )
}

export default PasswordChangeForm
